// MonthNavigationPicker.js
import React from 'react';
import {
  TextField,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import TodayIcon from '@mui/icons-material/Today';
import { format, isValid } from 'date-fns';
import { useCalendar } from '../calendarContext';
import { useMonthNavigator } from './useMonthNavigator';
import { styled } from '@mui/material/styles';
import { getNextOccurrence } from '../../../../util/booking/netxtOccurrence';

// StyledTextField with CSS that completely hides the clear button
const StyledTextField = styled(TextField)(({ theme }) => ({
  borderRadius: '8px',
  '& .MuiInputBase-root': { color: '#333' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc', borderRadius: '8px' },
    '&:hover fieldset': { borderColor: '#888' },
    '&.Mui-focused fieldset': { borderColor: '#4caf50' },
  },
}));

export const MonthYearSelector = ({ checkavailability, bookingType }) => {
  const { currentMonth, setCurrentMonth, setSelectedDate } = useCalendar();
  const { goToPreviousMonth, goToNextMonth, goToToday } = useMonthNavigator({
    currentMonth,
    setCurrentMonth,
    setSelectedDate,
  });

  const nextMonthOccurrence =
    checkavailability &&
    getNextOccurrence(
      currentMonth,
      bookingType.start,
      bookingType.recurring,
      bookingType.recurringInterval,
      bookingType.end
    );

  // Handle changes from the month input.
  const handleMonthChange = (e) => {
    const value = e.target.value;
  if (!value) return; // Prevent updating if cleared
  const [year, month] = value.split('-').map(Number);
  const newDate = new Date(year, month - 1, 1);

  // Get today's month and year.
  const today = new Date();
  if (
    newDate.getFullYear() === today.getFullYear() &&
    newDate.getMonth() === today.getMonth()
  ) {
    // If the selected month is the current month, trigger goToToday,
    // which should update both currentMonth and selectedDate.
    goToToday();
  } else {
  // Update both the current month and the selected date.
    setCurrentMonth(newDate);
    setSelectedDate(newDate);
  }
};


  // Format currentMonth if valid.
  const formattedValue =
    currentMonth && isValid(currentMonth) ? format(currentMonth, 'yyyy-MM') : '';

  return (
    <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
      {/* Left arrow button */}
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          goToPreviousMonth();
        }}
        aria-label="Previous Month"
        sx={{ color: 'lightblue' }}
      >
        <ArrowBack />
      </IconButton>

      {/* Month-Year Picker without an adornment */}
      <StyledTextField
        type="month"
        value={formattedValue}
        onChange={handleMonthChange}
        variant="outlined"
        InputProps={{
          sx: { maxWidth: '300px' },
        }}
      />

      {/* Existing "This Month" button placed outside the control */}
      <Tooltip title="This Month">
        <IconButton
          onClick={goToToday}
          aria-label="This Month"
          sx={{ color: 'lightblue' }}
        >
          <TodayIcon />
        </IconButton>
      </Tooltip>

      {/* Right arrow button */}
      <Tooltip title={nextMonthOccurrence ? 'Next Month' : 'No next occurrence'}>
        {/* Wrapping in a span ensures the tooltip works even if IconButton is disabled */}
        <span>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goToNextMonth();
            }}
            aria-label="Next Month"
            disabled={!nextMonthOccurrence && checkavailability}
            sx={{ color: 'lightblue' }}
          >
            <ArrowForward />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default MonthYearSelector;
